/**
 * Single source of truth for framework metadata across the frontend.
 *
 * Adding a new framework here automatically makes it available to:
 *   - OrgProvider (active_frameworks scope)
 *   - SoA, GapAnalysis, Benchmarking, CertificationReadiness, Reports,
 *     ImplementationWizard, ControlLibrary, Mappings, PolicyGenerator,
 *     Documents, ClientDashboard
 */

export const FRAMEWORKS = [
  // Core / IT compliance
  { id: "iso27001",  label: "ISO 27001:2022", short: "ISO 27001",  color: "#3b82f6", group: "core",         category: "Information Security" },
  { id: "nist_scf",  label: "NIST SCF",       short: "NIST SCF",   color: "#8b5cf6", group: "core",         category: "Cybersecurity" },
  { id: "cis_v8",    label: "CIS Controls v8.1", short: "CIS v8",  color: "#f97316", group: "core",         category: "Cybersecurity" },
  { id: "iso20000",  label: "ISO 20000-1",    short: "ISO 20000",  color: "#14b8a6", group: "core",         category: "IT Service Management" },

  // International reference frameworks — cross-mapping targets for NORTIC.
  // S18/ADR (D-S17-001, G6): these are NOT auditable-in-themselves under
  // ISO 19011 — they exist only as mapping targets. `referenceOnly: true`
  // excludes them from the audit-creation picker (see AUDITABLE_FRAMEWORKS
  // below) without touching cross-mapping or any other consumer of FRAMEWORKS.
  { id: "nist_csf_2", label: "NIST CSF 2.0",    short: "NIST CSF 2.0", color: "#a855f7", group: "core", category: "Cybersecurity (NIST reference)",
    authority: "NIST", edition: "2.0", published: "2024-02", referenceOnly: true },
  { id: "iso_31000",  label: "ISO 31000:2018",  short: "ISO 31000",    color: "#ec4899", group: "core", category: "Risk Management (reference)",
    authority: "ISO", edition: "2018", published: "2018-02", referenceOnly: true },
  { id: "iso_22301",  label: "ISO 22301:2019",  short: "ISO 22301",    color: "#f43f5e", group: "core", category: "Business Continuity (reference)",
    authority: "ISO", edition: "2019", published: "2019-10", referenceOnly: true },

  // RD LATAM — Dominican Republic regulatory norms (Sprint S1+)
  { id: "nortic_a7", label: "NORTIC A7:2025",    short: "NORTIC A7",  color: "#0ea5e9", group: "rd_latam",    category: "Gobierno RD — Seguridad de la Información",
    authority: "OGTIC", edition: "2da ed. v2.0", published: "2025-12" },
  { id: "nortic_a8", label: "NORTIC A8:2025",    short: "NORTIC A8",  color: "#0284c7", group: "rd_latam",    category: "Gobierno RD — Ciberseguridad",
    authority: "OGTIC + CNCS", edition: "1ra ed. v1.0", published: "2025-12" },
  { id: "nortic_a9", label: "NORTIC A9:2025",    short: "NORTIC A9",  color: "#0369a1", group: "rd_latam",    category: "Gobierno RD — Gestión de Riesgos y Continuidad",
    authority: "OGTIC + CNCS", edition: "1ra ed. v1.0", published: "2025-12" },

  // Sectorial — verticales regulados RD (Banca, Telco)
  { id: "jm_18110102", label: "Reglamento JM 181101-02", short: "JM 181101-02", color: "#b91c1c", group: "sectorial", category: "Banca RD — Seguridad Cibernética",
    authority: "Junta Monetaria (BCRD) / Superintendencia de Bancos", edition: "Segunda Resolución 2018", published: "2018-11" },

  // Module 16 — Zona Franca (Dominican Republic Free Trade Zone)
  { id: "oea",       label: "OEA",                short: "OEA",        color: "#f59e0b", group: "zona_franca", category: "Customs / Trade" },
  { id: "cnzfe",     label: "CNZFE 522-06",       short: "CNZFE",      color: "#14b8a6", group: "zona_franca", category: "Free Trade Zone" },
  { id: "iso22000",  label: "ISO 22000:2018",     short: "ISO 22000",  color: "#3b82f6", group: "zona_franca", category: "Food Safety" },
  { id: "bpm",       label: "BPM",                short: "BPM",        color: "#8b5cf6", group: "zona_franca", category: "Manufacturing" },
  { id: "haccp",     label: "HACCP",              short: "HACCP",      color: "#ef4444", group: "zona_franca", category: "Food Safety / HACCP" },
  { id: "nordom477", label: "NORDOM 477",         short: "NORDOM 477", color: "#06b6d4", group: "zona_franca", category: "DR Quality Standard" },
];

export const SUPPORTED_FRAMEWORKS = FRAMEWORKS.map((f) => f.id);

export const CORE_FRAMEWORKS = FRAMEWORKS.filter((f) => f.group === "core");
export const RD_LATAM_FRAMEWORKS = FRAMEWORKS.filter((f) => f.group === "rd_latam");
export const ZONA_FRANCA_FRAMEWORKS = FRAMEWORKS.filter((f) => f.group === "zona_franca");
export const SECTORIAL_FRAMEWORKS = FRAMEWORKS.filter((f) => f.group === "sectorial");

// S18 (D-S17-001, G6): frameworks that can be selected as ISO 19011 audit
// criteria — excludes cross-mapping-only reference frameworks (NIST CSF 2.0,
// ISO 31000, ISO 22301). Use this (not FRAMEWORKS) for the audit-creation
// picker; keep using FRAMEWORKS/FW_NAMES for display/lookup of existing
// audits and for cross-mapping, which are unaffected.
export const AUDITABLE_FRAMEWORKS = FRAMEWORKS.filter((f) => !f.referenceOnly);

// Lookup maps (use when a page expects {id: label} or {id: color})
export const FW_NAMES = Object.fromEntries(FRAMEWORKS.map((f) => [f.id, f.label]));
export const FW_SHORT = Object.fromEntries(FRAMEWORKS.map((f) => [f.id, f.short]));
export const FW_COLORS = Object.fromEntries(FRAMEWORKS.map((f) => [f.id, f.color]));
export const FW_GROUPS = Object.fromEntries(FRAMEWORKS.map((f) => [f.id, f.group]));

export const getFrameworkLabel = (id) => FW_NAMES[id] || id;
export const getFrameworkColor = (id) => FW_COLORS[id] || "#64748b";
export const isZonaFranca = (id) => FW_GROUPS[id] === "zona_franca";
export const isRdLatam = (id) => FW_GROUPS[id] === "rd_latam";
